/**
 * AGROMX Frontend - Auth Service
 * Servicio de autenticación
 */

import api from './api.js';

class AuthService {
  /**
   * Login
   */
  async login(username, password) {
    try {
      const response = await api.post('/auth/login', { username, password });
      
      // Guardar tokens
      if (response.data.accessToken) {
        api.setToken(response.data.accessToken);
        localStorage.setItem('refreshToken', response.data.refreshToken);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        // Inicio de sesion: desde aqui el cliente puede filtrar notificaciones de tracking (API / push en servidor)
        try {
          sessionStorage.setItem('agromx_tracking_notify_since', new Date().toISOString());
        } catch (e) {
          /* ignore */
        }
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Logout
   */
  async logout() {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      // No llamar unsubscribeBrowserOnly(): borra la suscripcion Push en el navegador y en el
      // siguiente login FCM genera un endpoint nuevo -> filas duplicadas en push_subscriptions.
      // El backend ya desactiva todas las filas del usuario (activo=0); al iniciar sesion de nuevo
      // init() reenvia la misma suscripcion y el UPSERT por endpoint reactiva una sola fila.
      // Limpiar storage siempre
      api.setToken(null);
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      try {
        sessionStorage.removeItem('agromx_tracking_notify_since');
      } catch (e) {
        /* ignore */
      }
      window.location.href = '/';
    }
  }

  /**
   * Obtener usuario actual
   */
  async getCurrentUser() {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Actualizar perfil
   */
  async updateProfile(data) {
    try {
      const response = await api.put('/auth/profile', data);
      
      // Actualizar usuario en localStorage
      const currentUser = this.getUser();
      const updatedUser = { ...currentUser, ...response.data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Cambiar contraseña
   */
  async changePassword(currentPassword, newPassword, confirmPassword) {
    try {
      const response = await api.put('/auth/password', {
        currentPassword,
        newPassword,
        confirmPassword
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Refresh token
   */
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token');
      }

      const response = await api.post('/auth/refresh', { refreshToken });
      
      // Actualizar access token
      if (response.data.accessToken) {
        api.setToken(response.data.accessToken);
      }

      return response.data;
    } catch (error) {
      // Si falla el refresh, hacer logout
      this.logout();
      throw error;
    }
  }

  /**
   * Verificar si está autenticado
   */
  isAuthenticated() {
    return !!api.getToken();
  }

  /**
   * Obtener usuario del localStorage
   */
  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Verificar si tiene un rol específico
   */
  hasRole(role) {
    const user = this.getUser();
    return user && user.rol === role;
  }

  /**
   * Verificar si tiene alguno de los roles
   */
  hasAnyRole(roles) {
    const user = this.getUser();
    return user && roles.includes(user.rol);
  }
}

export default new AuthService();
